// Sand depth map. A 2D Float32Array sized to viewport/2 holds per-cell
// "indentation" depth in [0..1]. The cursor's rake (3 parallel lines)
// imprints Gaussian deposits each frame; every frame the entire map decays
// by ~0.9985 so a path stays visible ~20–40 seconds before fading.

export type DepthMap = {
  width: number;
  height: number;
  scale: number;
  data: Float32Array;
};

const DECAY = 0.9985;
const BRUSH_RADIUS_CELLS = 4;
const RAKE_INNER_OFFSET_CELLS = 3;
const RAKE_OUTER_OFFSET_CELLS = 6;
const RAKE_CENTER_DEPTH = 1.0;
const RAKE_SIDE_DEPTH = 0.7;
const RAKE_OUTER_DEPTH = 0.4;

export function createDepthMap(
  viewportW: number,
  viewportH: number,
  scale = 2,
): DepthMap {
  const width = Math.max(2, Math.ceil(viewportW / scale));
  const height = Math.max(2, Math.ceil(viewportH / scale));
  return {
    width,
    height,
    scale,
    data: new Float32Array(width * height),
  };
}

function depositOne(map: DepthMap, px: number, py: number, peak: number) {
  const cx = px / map.scale;
  const cy = py / map.scale;
  const r = BRUSH_RADIUS_CELLS;
  const x0 = Math.max(0, Math.floor(cx - r));
  const x1 = Math.min(map.width - 1, Math.ceil(cx + r));
  const y0 = Math.max(0, Math.floor(cy - r));
  const y1 = Math.min(map.height - 1, Math.ceil(cy + r));
  const sigmaSq = (r * 0.6) * (r * 0.6);
  for (let y = y0; y <= y1; y++) {
    for (let x = x0; x <= x1; x++) {
      const dx = x - cx;
      const dy = y - cy;
      const fall = Math.exp(-(dx * dx + dy * dy) / (2 * sigmaSq));
      const idx = y * map.width + x;
      map.data[idx] = Math.min(1, map.data[idx] + peak * fall * 0.28);
    }
  }
}

export function imprintRake(
  map: DepthMap,
  px: number,
  py: number,
  vx: number,
  vy: number,
) {
  const speed = Math.hypot(vx, vy);
  if (speed < 0.5) return;
  const nx = -vy / speed;
  const ny = vx / speed;
  const inner = RAKE_INNER_OFFSET_CELLS * map.scale;
  const outer = RAKE_OUTER_OFFSET_CELLS * map.scale;
  depositOne(map, px, py, RAKE_CENTER_DEPTH);
  depositOne(map, px + nx * inner, py + ny * inner, RAKE_SIDE_DEPTH);
  depositOne(map, px - nx * inner, py - ny * inner, RAKE_SIDE_DEPTH);
  depositOne(map, px + nx * outer, py + ny * outer, RAKE_OUTER_DEPTH);
  depositOne(map, px - nx * outer, py - ny * outer, RAKE_OUTER_DEPTH);
}

export function imprintTap(map: DepthMap, px: number, py: number) {
  depositOne(map, px, py, 1.4);
}

// Stamps a pre-baked footprint ImageData (from lib/sand/Footprint.ts) onto
// the depth grid at (px, py) in viewport pixels, rotated by `angleRad`. The
// stamp source encodes depth in its alpha channel (0..255 → 0..1).
//
// Performance: each footprint touches ~120-200 depth cells which is far
// cheaper than the previous rake (which wrote on every pointermove frame).
export function stampFootprint(
  map: DepthMap,
  stamp: ImageData,
  px: number,
  py: number,
  angleRad: number,
  scale01 = 1,
) {
  const sw = stamp.width;
  const sh = stamp.height;
  const sox = sw / 2;
  const soy = sh / 2;
  const cos = Math.cos(angleRad + Math.PI / 2);
  const sin = Math.sin(angleRad + Math.PI / 2);
  // Iterate over an axis-aligned bbox big enough to cover the rotated stamp
  const bound = Math.ceil(Math.max(sw, sh) / 2 / map.scale) + 1;
  const cx = px / map.scale;
  const cy = py / map.scale;
  const x0 = Math.max(0, Math.floor(cx - bound));
  const x1 = Math.min(map.width - 1, Math.ceil(cx + bound));
  const y0 = Math.max(0, Math.floor(cy - bound));
  const y1 = Math.min(map.height - 1, Math.ceil(cy + bound));
  for (let y = y0; y <= y1; y++) {
    for (let x = x0; x <= x1; x++) {
      const dx = (x - cx) * map.scale;
      const dy = (y - cy) * map.scale;
      // Inverse rotation to sample the stamp at the right pixel
      const lx = dx * cos + dy * sin + sox;
      const ly = -dx * sin + dy * cos + soy;
      if (lx < 0 || lx >= sw - 1 || ly < 0 || ly >= sh - 1) continue;
      // Nearest-neighbor sample — bilinear adds cost for marginal smoothness
      const sx = Math.floor(lx);
      const sy = Math.floor(ly);
      const a = stamp.data[(sy * sw + sx) * 4 + 3] / 255;
      if (a <= 0.01) continue;
      const idx = y * map.width + x;
      map.data[idx] = Math.min(1, map.data[idx] + a * scale01);
    }
  }
}

export function decay(map: DepthMap) {
  const data = map.data;
  for (let i = 0; i < data.length; i++) data[i] *= DECAY;
}

export function fadeRegion(
  map: DepthMap,
  px: number,
  py: number,
  radiusPx: number,
  factor: number,
) {
  const cx = px / map.scale;
  const cy = py / map.scale;
  const r = radiusPx / map.scale;
  const x0 = Math.max(0, Math.floor(cx - r));
  const x1 = Math.min(map.width - 1, Math.ceil(cx + r));
  const y0 = Math.max(0, Math.floor(cy - r));
  const y1 = Math.min(map.height - 1, Math.ceil(cy + r));
  for (let y = y0; y <= y1; y++) {
    for (let x = x0; x <= x1; x++) {
      const dx = x - cx;
      const dy = y - cy;
      const d = Math.hypot(dx, dy);
      if (d > r) continue;
      const ease = 1 - d / r;
      const idx = y * map.width + x;
      map.data[idx] *= factor + (1 - factor) * (1 - ease);
    }
  }
}

export function resetWave(map: DepthMap, progress01: number) {
  const cutoff = Math.floor(progress01 * map.width);
  for (let y = 0; y < map.height; y++) {
    const rowStart = y * map.width;
    for (let x = 0; x < cutoff; x++) {
      map.data[rowStart + x] = 0;
    }
  }
}
